import type { PlantStatusDataStream } from "@sunner-cloud/api-client";

import type { PlantStatus } from "./PlantStatus";

const removeZeroValues = (obj: Record<string, number>) =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== 0));

const calculateTotalActiveAlarms = (
  alarms: Record<string, number>,
  communicationAlarms: number,
): number => {
  const alarmsSum = Object.values(alarms).reduce(
    (sum, count) => sum + count,
    0,
  );
  return alarmsSum + communicationAlarms;
};

const hasActiveAlarms = (
  withAlarms: number,
  communicationAlarms: number,
): boolean => withAlarms > 0 || communicationAlarms > 0;

interface DeviceAlarmEntry {
  withAlarms: number;
  communicationAlarms: number;
  totalActiveAlarms: number;
  filteredAlarms: Record<string, number>;
}

const buildDeviceAlarmEntry = (
  entry: DeviceAlarmEntry,
): Record<string, unknown> => ({
  withAlarms: entry.withAlarms,
  totalActiveAlarms: entry.totalActiveAlarms,
  ...(entry.communicationAlarms > 0 && {
    communicationAlarms: entry.communicationAlarms,
  }),
  ...(Object.keys(entry.filteredAlarms).length && {
    alarms: entry.filteredAlarms,
  }),
});

const mapTcuAlarms = (
  response: PlantStatusDataStream,
  result: PlantStatus,
): void => {
  if (!response.tcus) {
    return;
  }
  const alarms = {
    tracking: response.tcus.alarms?.tracking ?? 0,
    motor: response.tcus.alarms?.motor ?? 0,
    battery: response.tcus.alarms?.battery ?? 0,
    hardware: response.tcus.alarms?.hardware ?? 0,
  };
  const communicationAlarms = response.tcus.communication_alarms;
  if (!hasActiveAlarms(response.tcus.with_alarms, communicationAlarms)) {
    return;
  }
  result.tcus = buildDeviceAlarmEntry({
    withAlarms: response.tcus.with_alarms,
    communicationAlarms,
    totalActiveAlarms: calculateTotalActiveAlarms(alarms, communicationAlarms),
    filteredAlarms: removeZeroValues(alarms),
  }) as PlantStatus["tcus"];
};

const mapHsuAlarms = (
  response: PlantStatusDataStream,
  result: PlantStatus,
): void => {
  if (!response.hsus) {
    return;
  }
  const alarms = {
    hardware: response.hsus.alarms?.hardware ?? 0,
  };
  const communicationAlarms = response.hsus.communication_alarms;
  if (!hasActiveAlarms(response.hsus.with_alarms, communicationAlarms)) {
    return;
  }
  result.hsus = buildDeviceAlarmEntry({
    withAlarms: response.hsus.with_alarms,
    communicationAlarms,
    totalActiveAlarms: calculateTotalActiveAlarms(alarms, communicationAlarms),
    filteredAlarms: removeZeroValues(alarms),
  });
};

const mapHsuWarnings = (
  response: PlantStatusDataStream,
  result: PlantStatus,
): void => {
  if (!response.hsus) {
    return;
  }
  const warnings = {
    wind: response.hsus.warnings?.wind ?? 0,
    snow: response.hsus.warnings?.snow ?? 0,
    solarRadiation: response.hsus.warnings?.solar_radiation ?? 0,
  };
  const hasWarnings =
    (response.hsus.with_warnings ?? 0) > 0 ||
    Object.values(warnings).some((v) => v > 0);

  if (!hasWarnings) {
    return;
  }
  const filteredWarnings = removeZeroValues(warnings);
  const totalActiveWarnings = Object.values(warnings).reduce(
    (sum, count) => sum + count,
    0,
  );
  result.hsus ??= {};
  result.hsus.withWarnings = response.hsus.with_warnings;
  result.hsus.totalActiveWarnings = totalActiveWarnings;
  if (Object.keys(filteredWarnings).length) {
    result.hsus.warnings = filteredWarnings;
  }
};

const mapNcuAlarms = (
  response: PlantStatusDataStream,
  result: PlantStatus,
): void => {
  if (!response.ncus) {
    return;
  }
  const alarms = {
    gateway: response.ncus.alarms?.gateway ?? 0,
    noAcPower: response.ncus.alarms?.no_ac_power ?? 0,
    lowBattery: response.ncus.alarms?.low_battery ?? 0,
  };
  const communicationAlarms = response.ncus.communication_alarms;
  if (!hasActiveAlarms(response.ncus.with_alarms, communicationAlarms)) {
    return;
  }
  result.ncus = buildDeviceAlarmEntry({
    withAlarms: response.ncus.with_alarms,
    communicationAlarms,
    totalActiveAlarms: calculateTotalActiveAlarms(alarms, communicationAlarms),
    filteredAlarms: removeZeroValues(alarms),
  }) as PlantStatus["ncus"];
};

export const mapPlantStatus = (
  response: PlantStatusDataStream,
): PlantStatus => {
  const result: PlantStatus = {
    plantId: response.plant_id,
    controlMode: response.control_mode,
  };

  mapTcuAlarms(response, result);
  mapHsuAlarms(response, result);
  mapHsuWarnings(response, result);
  mapNcuAlarms(response, result);

  return result;
};
